/*
 * transcript-file.js — window.__transcriptFile
 * ─────────────────────────────────────────────────────────────────────────────
 * ОДИН источник правды «расшифровка → файл»: как режутся абзацы, как выглядит
 * текст с говорящими и таймкодами, как называется файл и что написано ВНУТРИ
 * документа.
 *
 * Зачем модуль. У расшифровки два дома — страница тула и «Мои генерации», — и
 * каждый успел завести своё представление о том, что значит «отдай файл»:
 *
 *   страница тула   7 форматов, сборка в браузере, имя-константа transcription.*
 *   библиотека      3 формата, сборка на сервере, имя по исходной записи
 *
 * То есть один и тот же текст уезжал под двумя разными именами и в разном
 * оформлении, смотря откуда его взяли. Это ровно та дивергенция, которую в
 * проекте лечат общим примитивом (см. таблицу «Shared components» в CLAUDE.md).
 *
 * ⚠️ ИМЯ ФАЙЛА — НЕ КОСМЕТИКА. Все семь экспортов тула звали файл
 * `transcription.docx`. Замер 06.09.2026: 41 владелец из 69 держит ≥2
 * расшифровки, 20 — ≥5; 16 % сессий экспортируют ≥2 файла подряд, то есть
 * гарантированно получают «transcription (1).docx». У пользовательницы, ради
 * жалобы которой это писалось, 16 записей консультаций — в папке загрузок
 * телефона они все назывались одинаково. Класс уже описан в
 * output-filename.js: «имя выхода обязано нести материал».
 *
 * ⚠️ И ЗАГОЛОВОК ВНУТРИ ДОКУМЕНТА ТОЖЕ. В DOCX уходило слово «Расшифровка» и
 * дата ЭКСПОРТА. Открыв такой файл через полгода, человек не узнаёт, чья это
 * запись: имя записи не попадало в документ вообще, а дата отвечала на вопрос
 * «когда я нажал кнопку», а не «когда это было записано».
 *
 * UMD: браузер (window.__transcriptFile) + Node (module.exports), чтобы правила
 * проверялись тестом без браузера — тот же контракт, что у output-filename.js.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory(require('./output-filename.js'));
    else root.__transcriptFile = factory(root.__outputName);
})(typeof self !== 'undefined' ? self : this, function (outputName) {
    'use strict';

    /* ── Абзацы ───────────────────────────────────────────────────────────────
       Перенесено со страницы тула (2026-09-04) БЕЗ изменения правил: абзац
       начинается на смене говорящего, на длинной паузе или — внутри монолога —
       на конце предложения, когда блок уже дорос до читаемого. Пороги замерены
       на 2.5-часовой лекции: медиана паузы 0.74 с, p90 1.96 с, поэтому 2.5 с —
       настоящая пауза, а 1.0 с после точки — вдох. */
    var PARA_GAP_HARD = 2.5, PARA_GAP_SOFT = 1.0,
        PARA_SOFT_CHARS = 350, PARA_HARD_CHARS = 700, PARA_MAX_CHARS = 1200;

    function endsSentence(t) { return /[.!?…][»"”)]?\s*$/.test(t || ''); }

    function paragraphs(segs) {
        var paras = [], cur = null, chars = 0, prev = null;
        segs = segs || [];
        for (var i = 0; i < segs.length; i++) {
            var s = segs[i];
            if (s && s.isParagraph) { if (cur) { paras.push(cur); cur = null; } prev = null; chars = 0; continue; }
            if (!s || !s.text) continue;
            var start = s.timestamp || 0;
            var spk = s.speaker || (cur ? cur.speaker : null);
            var brk = !cur;
            if (cur) {
                if (spk && spk !== cur.speaker) brk = true;
                else {
                    var prevEnd = (prev && typeof prev.end === 'number') ? prev.end : (prev ? prev.timestamp : start);
                    var gap = start - prevEnd;
                    var sentenceEnd = prev ? endsSentence(prev.text) : true;
                    if (gap >= PARA_GAP_HARD) brk = true;
                    else if (chars >= PARA_MAX_CHARS) brk = true;
                    else if (sentenceEnd && (chars >= PARA_HARD_CHARS || (chars >= PARA_SOFT_CHARS && gap >= PARA_GAP_SOFT))) brk = true;
                }
            }
            if (brk) { if (cur) paras.push(cur); cur = { speaker: spk, start: start, end: (typeof s.end === 'number') ? s.end : start, segs: [] }; chars = 0; }
            cur.segs.push(s);
            if (typeof s.end === 'number') cur.end = s.end;
            chars += s.text.length + 1;
            prev = s;
        }
        if (cur) paras.push(cur);
        return paras;
    }

    function clock(sec) {
        sec = Math.max(0, Math.round(sec || 0));
        var h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
        var mm = (m < 10 ? '0' : '') + m, ss = (s < 10 ? '0' : '') + s;
        return h > 0 ? h + ':' + mm + ':' + ss : mm + ':' + ss;
    }

    /* Метка говорящего — только там, где говорящий МЕНЯЕТСЯ: монолог, разбитый
       на абзацы, не должен повторять «Говорящий 1» на каждом. */
    function toText(paras, opts) {
        opts = opts || {};
        var out = [], prevSpk = null;
        for (var i = 0; i < paras.length; i++) {
            var p = paras[i], head = [];
            if (opts.timestamps) head.push('[' + clock(p.start || 0) + ']');
            if (opts.speakers && p.speaker && p.speaker !== prevSpk) head.push(p.speaker + ':');
            if (p.speaker) prevSpk = p.speaker;
            var body = p.segs.map(function (s) { return s.text; }).join(' ').replace(/\s+/g, ' ').trim();
            out.push((head.length ? head.join(' ') + ' ' : '') + body);
        }
        return out.join('\n\n').trim();
    }

    /**
     * Абзацы как СТРУКТУРА, а не как склеенная строка: {time, speaker, text}.
     *
     * Зачем отдельно от text(). Экспорт в Word получал уже готовую строку
     * «[00:12] Говорящий 2: …» и клал её одним прогоном обычного текста. Данные
     * в файле были — не было ВИДА: на экране метка это цветная пилюля на своей
     * строке, а в Word та же метка идёт в теле абзаца тем же шрифтом, и файл
     * читается как сплошная проза. Именно так это и описал человек: «в окошке
     * текст с разделением на говорящих, при скачивании сплошной» (06.09.2026,
     * Премиум) — при том что метки в файле ПРИСУТСТВОВАЛИ (замер: её выгрузка
     * на 733 знака длиннее склейки без меток).
     *
     * Структура позволяет отдать имя отдельным жирным прогоном, а время —
     * серым: так делают Otter, Sonix и Trint, и так реплики видно глазом.
     */
    function blocks(segs, opts) {
        opts = opts || {};
        var paras = paragraphs(segs), out = [], prevSpk = null;
        for (var i = 0; i < paras.length; i++) {
            var p = paras[i];
            var body = p.segs.map(function (s) { return s.text; }).join(' ').replace(/\s+/g, ' ').trim();
            if (!body) continue;
            var spk = (opts.speakers && p.speaker && p.speaker !== prevSpk) ? p.speaker : null;
            if (p.speaker) prevSpk = p.speaker;
            out.push({
                time: opts.timestamps ? clock(p.start || 0) : null,
                speaker: spk,
                text: body,
            });
        }
        return out;
    }

    /** Сегменты → готовый текст одним вызовом (самый частый путь). */
    function text(segs, opts) { return toText(paragraphs(segs), opts); }

    /* ── Имя файла ────────────────────────────────────────────────────────────
       «беседа с Леной.m4a» + docx → «беседа с Леной — расшифровка.docx».
       Расширение исходника снимается (иначе выйдет «…m4a — расшифровка.docx»),
       чистка и потолок в БАЙТАХ — общим примитивом: кириллица вдвое длиннее, и
       120 символов имени легко переваливают за лимит файловой системы.

       Пустой источник (микрофонная запись — файла не было) даёт «расшифровка
       06.09.2026.docx»: дата тут ОСМЫСЛЕННА, потому что материал сам по себе
       безымянный, — в отличие от файла, у которого имя есть. */
    function name(sourceName, fmt, opts) {
        opts = opts || {};
        var word = opts.word || 'transcript';
        var on = (typeof outputName !== 'undefined' && outputName) ? outputName : null;
        var base = String(sourceName == null ? '' : sourceName).trim();
        if (base && on) base = on.split(base).base;
        else if (base) base = base.replace(/\.[A-Za-z0-9]{1,8}$/, '');
        base = base.trim();

        var out = base ? base + ' — ' + word : word + ' ' + (opts.date || todayStamp());
        if (opts.suffix) out += ' ' + opts.suffix;
        if (on) {
            var joined = on.join(out, fmt);
            if (joined) return joined;
        }
        return out.replace(/[\\/:*?"<>|]+/g, '_') + '.' + fmt;
    }

    function todayStamp(d) {
        d = d || new Date();
        var p = function (n) { return (n < 10 ? '0' : '') + n; };
        return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear();
    }

    /* ── Что написано ВНУТРИ документа ────────────────────────────────────────
       Заголовок — имя записи (по нему человек узнаёт свой материал), а не слово
       «Расшифровка», которое одинаково у всех файлов в папке. Подзаголовок —
       длительность записи и дата, если они известны: «2 ч 12 мин · 14.08.2026».
       Слово «Расшифровка» уходит в подзаголовок — оно объясняет ЧТО это, но не
       годится на роль имени. */
    function heading(sourceName, opts) {
        opts = opts || {};
        var base = String(sourceName == null ? '' : sourceName).trim();
        var on = (typeof outputName !== 'undefined' && outputName) ? outputName : null;
        if (base && on) base = on.split(base).base;
        else if (base) base = base.replace(/\.[A-Za-z0-9]{1,8}$/, '');
        base = base.trim();
        return base || opts.fallback || 'Transcript';
    }

    /**
     * Длительность записи словами — по ШАБЛОНАМ вызывающего, а не по вшитым
     * «ч»/«мин»: модуль работает на десяти локалях, и собственный русский текст
     * внутри него уехал бы в корейский документ. Шаблоны те же, что у страницы
     * «Мои генерации» (generations.transcript_len_h / _m), плейсхолдеры {h}/{m}.
     */
    function humanLength(sec, tpl) {
        sec = Math.max(0, Math.round(sec || 0));
        if (!sec) return '';
        tpl = tpl || {};
        var h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
        if (m === 60) { h += 1; m = 0; }
        if (h > 0) {
            return String(tpl.h || '{h} h {m} min').replace('{h}', h).replace('{m}', m);
        }
        return String(tpl.m || '{m} min').replace('{m}', Math.max(1, m));
    }

    /** Строка под заголовком: «Расшифровка · 2 ч 12 мин · 14.08.2026». */
    function subtitle(parts) {
        parts = parts || {};
        var bits = [];
        if (parts.word) bits.push(parts.word);
        var len = parts.length || (parts.durationSec ? humanLength(parts.durationSec, parts.lenTpl) : '');
        if (len) bits.push(len);
        if (parts.date) bits.push(parts.date);
        return bits.join(' · ');
    }

    return {
        paragraphs: paragraphs,
        blocks: blocks,
        toText: toText,
        text: text,
        name: name,
        heading: heading,
        subtitle: subtitle,
        clock: clock,
        humanLength: humanLength,
        todayStamp: todayStamp,
    };
});
